import { formatCurrency } from '@if/core/utils'
import { DataField, StrongValue } from '@if/mui'
import { Grid } from '@mui/material'
import { getContractExpirationDate } from '@src/data/creditapp-selectors'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  creditApp: CreditApplication
  isExpiryFeatureActive?: boolean
}
const ContractInformation = ({ creditApp, isExpiryFeatureActive = false }: Props) => {
  const { t } = useTranslation()
  const expiresAt = isExpiryFeatureActive ? getContractExpirationDate(creditApp) : null
  const isExpired = expiresAt != null && expiresAt < new Date()
  const expirationDateText = expiresAt ? expiresAt.toISOString().slice(0, 10) : null
  const paymentFrequency = creditApp.worksheet?.paymentFrequency
    ? t(`EPaymentFrequency.${creditApp.worksheet.paymentFrequency}`, { ns: 'enum' })
    : null
  const paymentAmountForFrequency = creditApp.worksheet?.paymentAmountForFrequency
    ? formatCurrency(creditApp.worksheet.paymentAmountForFrequency)
    : null
  const loanTerm = creditApp.worksheet?.term ? `${creditApp.worksheet.term} ${t('common.months')}` : null

  const fields: { label: string; value: ReactNode }[] = useMemo(
    () => [
      { label: t('worksheet.activationDate'), value: creditApp.worksheet?.deliveryOn },
      { label: t('worksheet.paymentFrequency'), value: paymentFrequency },
      { label: t('worksheet.firstPaymentOn'), value: creditApp.worksheet?.firstPaymentOn },
      { label: t('worksheet.loanTerm'), value: loanTerm },
      { label: t('worksheet.paymentAmount'), value: paymentAmountForFrequency },
      { label: t('worksheet.requestedAmount'), value: formatCurrency(creditApp.worksheet?.amountRequested) },
      ...(expirationDateText
        ? [{ label: isExpired ? t('worksheet.expiredOn') : t('worksheet.expiresOn'), value: expirationDateText }]
        : []),
    ],
    [creditApp, t, isExpired, expirationDateText, paymentFrequency, paymentAmountForFrequency, loanTerm],
  )

  return (
    <Grid container spacing={2}>
      {fields.map((field) => (
        <Grid key={field.label} size={{ xs: 12, sm: 4 }}>
          <DataField
            label={field.label}
            value={field.value != null && field.value !== '' ? <StrongValue>{field.value}</StrongValue> : undefined}
          />
        </Grid>
      ))}
    </Grid>
  )
}

export default ContractInformation
